/**
 * The price model of Außencheck — owner's decision of 29.10.2026 (cap 30.10.2026), the one
 * place it is written down (docs/vertrieb.md explains it). The site renders
 * every price, example and percentage from here, and site.test.ts refuses a
 * "… €" on the site that this module does not produce. It lives in core since
 * the online sign-up (0024): the API states the fee in the contract
 * confirmation and the web previews it while the club types — the same
 * arithmetic in all three places.
 *
 * Amounts are gross (incl. VAT), in euro cents, per month. Only club-owned
 * aircraft count; a powered glider (Motorsegler) counts as a powered aircraft.
 */
export const PRICING = {
  /** Motorflugzeug, also Motorsegler. */
  poweredCents: 1000,
  /** Segelflugzeug. */
  gliderCents: 800,
  /** Ultraleichtflugzeug. */
  ulCents: 800,
  /**
   * No club pays more than this per month — the whole club, all departments
   * of its contract together (0026). 79 € until 30.10.2026, raised by the
   * owner before the first contract with the departments: „eher 99 EUR auf
   * Vereinsebene“.
   */
  capCents: 9900,
  /** Paying a year in advance instead of monthly. */
  yearlyDiscountPercent: 5,
  /**
   * Founding places in total (Pionierplätze; FSV 1910 holds the first).
   * While places are free a concluded contract takes one — the API hands them
   * out under a lock, ranks 1…founderClubs (contracts.founder_rank, 0024).
   */
  founderClubs: 10,
  founderDiscountPercent: 50,
  /** The operator may (not must) raise prices by up to this much per contract year. */
  maxIncreasePercent: 5,
  vatPercent: 19,
} as const;

export interface Fleet {
  readonly powered?: number;
  readonly glider?: number;
  readonly ul?: number;
}

/** Sum of the per-aircraft fees, before the cap. */
export function uncappedMonthlyCents(f: Fleet): number {
  return (f.powered ?? 0) * PRICING.poweredCents + (f.glider ?? 0) * PRICING.gliderCents + (f.ul ?? 0) * PRICING.ulCents;
}

/** What a club pays per month, monthly billing. */
export function monthlyCents(f: Fleet): number {
  return Math.min(uncappedMonthlyCents(f), PRICING.capCents);
}

/** What a club pays for a year in advance (twelve months less the yearly discount), rounded to the cent. */
export function yearlyCents(f: Fleet): number {
  return Math.round((monthlyCents(f) * 12 * (100 - PRICING.yearlyDiscountPercent)) / 100);
}

/** The monthly fee of a founding club. */
export function founderMonthlyCents(f: Fleet): number {
  return Math.round((monthlyCents(f) * (100 - PRICING.founderDiscountPercent)) / 100);
}

/** "30 €", "900,60 €", "1.234 €": whole euros without decimals, a non-breaking space (U+00A0) before the sign. */
export function euro(cents: number): string {
  const whole = Math.floor(cents / 100);
  const rest = cents % 100;
  const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${grouped}${rest ? `,${String(rest).padStart(2, '0')}` : ''}\u00a0€`;
}

/** Interval a club pays in (contracts.payment_interval). */
export type PaymentInterval = 'monthly' | 'yearly';

/**
 * Aircraft a club declares when it signs up. A powered glider (TMG) counts as
 * a powered aircraft (Motorsegler). Billing later follows the aircraft the
 * club has registered in Außencheck, counted on the first of each month.
 */
export interface DeclaredFleet {
  readonly motor: number;
  readonly tmg: number;
  readonly glider: number;
  readonly ul: number;
}

export function fleetOf(d: DeclaredFleet): Fleet {
  return { powered: d.motor + d.tmg, glider: d.glider, ul: d.ul };
}

export interface Offer {
  /** List price per month before the cap. */
  readonly uncappedMonthlyCents: number;
  /** List price per month after the cap. */
  readonly monthlyCents: number;
  readonly capped: boolean;
  readonly founder: boolean;
  /** Per month after the founder discount (equals monthlyCents without one). */
  readonly effectiveMonthlyCents: number;
  /** What one invoice says: a month, or a year in advance less the yearly discount. */
  readonly interval: PaymentInterval;
  readonly perIntervalCents: number;
}

/**
 * The fee for a fleet: cap first, then the founder discount, then — paying a
 * year in advance — the yearly discount on twelve such months. Both discounts
 * apply together; each is rounded to the cent where it is taken.
 */
export function offerFor(fleet: Fleet, interval: PaymentInterval, founder: boolean): Offer {
  const uncapped = uncappedMonthlyCents(fleet);
  const monthly = monthlyCents(fleet);
  const effective = founder ? founderMonthlyCents(fleet) : monthly;
  const perInterval =
    interval === 'yearly' ? Math.round((effective * 12 * (100 - PRICING.yearlyDiscountPercent)) / 100) : effective;
  return {
    uncappedMonthlyCents: uncapped,
    monthlyCents: monthly,
    capped: uncapped > monthly,
    founder,
    effectiveMonthlyCents: effective,
    interval,
    perIntervalCents: perInterval,
  };
}

/** Free founding places, given how many contracts hold one. Never below zero. */
export function founderPlacesLeft(taken: number): number {
  return Math.max(0, PRICING.founderClubs - Math.max(0, Math.floor(taken)));
}

/** „Noch 9 Vereine können …“ — the sentence site and sign-up show. */
export function founderPlacesText(left: number): string {
  if (left <= 0) return 'Alle Pionierplätze sind vergeben.';
  return left === 1
    ? `Noch 1 Verein kann von ${PRICING.founderDiscountPercent} % Rabatt profitieren.`
    : `Noch ${left} Vereine können von ${PRICING.founderDiscountPercent} % Rabatt profitieren.`;
}
